import { Link } from 'react-router-dom';
import type { VideoItem } from '@/mocks/videos';
import { formatPlays } from '@/utils/videoUtils';

interface FeaturedVideosProps {
  videos: VideoItem[];
}

export default function FeaturedVideos({ videos }: FeaturedVideosProps) {
  const [main, ...rest] = videos;
  if (!main) return null;

  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
      <div className="mb-6">
        <h2 className="font-display text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-3">
          <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
          精选推荐
        </h2>
        <p className="mt-2 text-xs md:text-sm text-foreground-500 pl-4">
          编辑精选与高分佳作，为你挑选值得一看的好内容
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Link
          to={`/detail/${main.id}`}
          className="group relative rounded-lg overflow-hidden bg-secondary-950 h-72 md:h-auto md:min-h-[420px] cursor-pointer"
        >
          <img
            src={main.cover}
            alt={`${main.title}封面`}
            title={`${main.title} - 在线观看`}
            className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/95 via-secondary-950/40 to-transparent"></div>
          <div className="relative z-10 h-full flex flex-col justify-end p-5 md:p-7">
            <span className="self-start px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-semibold mb-3">
              编辑推荐
            </span>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-background-50">{main.title}</h3>
            <p className="mt-2 text-sm text-background-200 line-clamp-2 max-w-lg leading-relaxed">
              {main.description}
            </p>
            <div className="mt-3 flex items-center gap-4 text-xs text-background-300">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-400"></i>
                {main.rating.toFixed(1)}
              </span>
              <span>{main.year}</span>
              <span>{main.duration}</span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line text-accent-400"></i>
                {formatPlays(main.plays)}
              </span>
            </div>
          </div>
        </Link>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
          {rest.slice(0, 4).map((video) => (
            <Link
              key={video.id}
              to={`/detail/${video.id}`}
              className="group rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-all duration-300 cursor-pointer"
            >
              <div className="relative w-full aspect-video overflow-hidden bg-background-200">
                <img
                  src={video.cover}
                  alt={`${video.title}封面`}
                  title={`${video.title} - 在线观看`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-foreground-950/75 text-background-100 text-xs">
                  {video.duration}
                </span>
              </div>
              <div className="p-3">
                <h3 className="text-sm font-semibold text-foreground-950 line-clamp-1 group-hover:text-primary-700 transition-colors">
                  {video.title}
                </h3>
                <div className="mt-1.5 flex items-center justify-between text-xs text-foreground-500">
                  <span className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-800">{video.type}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    {video.rating.toFixed(1)}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}